const AI_INFO_SELECTOR = ".ai-info";
const TRIGGER_SELECTOR = "[data-ai-info-trigger]";
const POPOVER_SELECTOR = "[data-ai-info-popover]";
const VIEWPORT_GUTTER = 16;
const POPOVER_GAP = 10;

type AIInfoInstance = {
  element: HTMLElement;
  trigger: HTMLButtonElement;
  popover: HTMLElement;
};

let activeInstance: AIInfoInstance | null = null;
let positionFrame: number | null = null;
let areGlobalListenersReady = false;

const updatePosition = (instance: AIInfoInstance): void => {
  const { element, trigger, popover } = instance;
  if (popover.hidden || !element.isConnected) return;

  const elementRect = element.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const minLeft = window.scrollX + VIEWPORT_GUTTER;
  const maxLeft = window.scrollX + window.innerWidth - popoverRect.width - VIEWPORT_GUTTER;
  const desiredLeft = window.scrollX + elementRect.left;
  const left = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
  const top = window.scrollY + elementRect.top - popoverRect.height - POPOVER_GAP;
  const arrowLeft = window.scrollX + triggerRect.left + triggerRect.width / 2 - left;

  popover.style.left = `${left}px`;
  popover.style.top = `${top}px`;
  popover.style.setProperty("--ai-info-arrow-left", `${arrowLeft}px`);
};

const schedulePositionUpdate = (): void => {
  if (!activeInstance || positionFrame !== null) return;

  positionFrame = window.requestAnimationFrame(() => {
    positionFrame = null;
    if (activeInstance) updatePosition(activeInstance);
  });
};

const hideActive = (restoreFocus = false): void => {
  if (!activeInstance) return;

  const { trigger, popover } = activeInstance;
  popover.hidden = true;
  trigger.setAttribute("aria-expanded", "false");
  activeInstance = null;

  if (restoreFocus) trigger.focus({ preventScroll: true });
};

const show = (instance: AIInfoInstance): void => {
  if (activeInstance && activeInstance !== instance) hideActive();

  activeInstance = instance;
  instance.popover.hidden = false;
  instance.trigger.setAttribute("aria-expanded", "true");
  updatePosition(instance);
};

const initGlobalListeners = (): void => {
  if (areGlobalListenersReady) return;
  areGlobalListenersReady = true;

  document.addEventListener("click", (event) => {
    if (!activeInstance) return;

    const target = event.target as Node;
    const { trigger, popover } = activeInstance;

    if (!trigger.contains(target) && !popover.contains(target)) hideActive();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") hideActive(true);
  });

  window.addEventListener("resize", schedulePositionUpdate);
  window.addEventListener("scroll", schedulePositionUpdate, {
    capture: true,
    passive: true,
  });
};

export const initAIInfo = (root: ParentNode = document): void => {
  if (typeof document === "undefined") return;

  root.querySelectorAll<HTMLElement>(AI_INFO_SELECTOR).forEach((element) => {
    if (element.dataset.aiInfoInitialized === "true") return;

    const trigger = element.querySelector<HTMLButtonElement>(TRIGGER_SELECTOR);
    const popover = element.querySelector<HTMLElement>(POPOVER_SELECTOR);

    if (!trigger || !popover) return;

    initGlobalListeners();

    const instance = { element, trigger, popover };
    element.dataset.aiInfoInitialized = "true";
    document.body.appendChild(popover);

    trigger.addEventListener("click", (event) => {
      event.preventDefault();

      if (activeInstance === instance) {
        hideActive();
        return;
      }

      show(instance);
    });
  });
};
